import TitlePage from 'components/atoms/title-page'
import PropTypes from 'prop-types'
import React, { useEffect, useState } from 'react'
import { Card, Button, Badge, Row, Col } from 'reactstrap'
import { useHistory, useLocation } from 'react-router'
import ParamsUtil from 'utils/params-util'
import CustomerVoucherRulesApiV2 from 'api/v2/admins/customer-voucher-rules-api-v2'
import { VoucherRuleTable, VoucherRuleFilterForm } from 'domains/voucher/organisms'
import { Link } from 'react-router-dom'
import { SortAndPerPageFilters } from 'components/organisms'

const propTypes = {
  pageUtils: PropTypes.shape({
    setAlertMsg    : PropTypes.func,
    setApiErrorMsg : PropTypes.func
  })
}

const SORT_BY_OPTIONS = {
  ID: {
    label : 'Id',
    value : 'id'
  },
  NAME: {
    label : 'Name',
    value : 'name'
  },
  FROM_DATE: {
    label : 'Date Start',
    value : 'from_date'
  },
  TO_DATE: {
    label : 'Date Expire',
    value : 'to_date'
  },
  TIMES_USED: {
    label : 'Used',
    value : 'times_used'
  }
}

const DEFAULT_FILTERS = {
  page             : 1,
  per_page         : 10,
  name             : '',
  start_from_date  : '',
  start_to_date    : '',
  expire_from_date : '',
  expire_to_date   : '',
  is_active        : '',
  show_on_checkout : '',
  voucher_code     : '',
  sort_by          : 'id',
  sort_dir         : 'desc'
}

const defaultProps = {
  pageUtils: {
    setAlertMsg    : () => {},
    setApiErrorMsg : () => {}
  }
}

const VoucherListPage = ({ pageUtils }) => {
  const [ voucherRules, setVoucherRules ] = useState([])
  const [ pagination, setPagination ]     = useState({})
  const history                           = useHistory()
  const [ isLoading, setIsLoading ]       = useState(false)

  const location = useLocation()

  const filters  = Object.fromEntries(new URLSearchParams(location.search))
  const pathName = Object.fromEntries(new URLSearchParams(location)).pathname

  useEffect(() => {
    handleFetchVouchers()
  }, [ location.search ]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleFetchVouchers = () => {
    setIsLoading(true)
    let params          = DEFAULT_FILTERS
    const isEmptyParams = Object.keys(filters).length === 0

    if (!isEmptyParams) {
      params = filters
    }

    CustomerVoucherRulesApiV2.get(params)
      .then((response) => {
        setVoucherRules(response.data.rules)
        setPagination(response.data.meta)
      })
      .catch((error) => {
        pageUtils.setAlertMsg(error.response.data)
      })
      .finally( () => setIsLoading(false) )
  }

  const handleSubmitFilter = (values, formik) => {
    //remove empty attributes from params
    let params = ParamsUtil.removeEmptyAttributes(values)
    //reset page
    params = ParamsUtil.removeAttributeFromObject('page',params)

    history.push({
      pathname : pathName,
      search   : new URLSearchParams(params).toString()
    })

    formik.setSubmitting(false)
  }

  const handleResetFilter = () => history.push({ pathname: pathName })

  /**
   * Move to another table page
   * @param {Number} page - page number destination
   */
  const handlePageChange = (page) => {
    const params = filters
    //replace page value
    params[ 'page' ] = page

    history.push({
      pathname : pathName,
      search   : new URLSearchParams(params).toString()
    })
  }

  /**
   * Remove a filter from table and url params
   * @param {String} key - filter key
   */
  const handleClickRemoveFilter = (key) => {
    let params = filters
    //replace page value
    params[ key ] = ''
    params        = ParamsUtil.removeEmptyAttributes(params)

    history.push({
      pathname : pathName,
      search   : new URLSearchParams(params).toString()
    })
  }

  /**
   * Function that trigger when user click Table Row
   * @param {Number} id - Customer::Cart::Voucher::Rule.id
   */
  const handleTableRowClick = (id) => {
    history.push({
      pathname: `/app/vouchers/${id}`
    })
  }

  /**
   * Handles the change event for sorting.
   * @param {Event} event - The event object triggered by the change.
   */
  const handleSortFilterChange = (event) => {
    let params           = filters
    const urlParamsKey   = event.target.name
    const newFilterValue = event.target.value

    params[urlParamsKey] = newFilterValue
    //reset page
    params = ParamsUtil.removeAttributeFromObject('page',params)

    history.push({
      pathname : pathName,
      search   : new URLSearchParams(params).toString()
    })
  }

  return (
    <>
      <div className='d-flex justify-content-between align-items-center'>
        <TitlePage mainTitle='Vouchers' />
        <Button
          color='primary'
          tag={ Link }
          to={ '/app/vouchers/create' }
        >
          { 'Add New Voucher' }
        </Button>
      </div>
      <Row>
        <Col className='col-xl-2'>
          <VoucherRuleFilterForm
            onClickSubmitForm={ handleSubmitFilter }
            filterValues={ filters }
            onClickReset={ handleResetFilter }
          />
        </Col>

        <Col className='col-xl-10' >
          <div className='mb-3'>
            <h5 className='text-muted text-uppercase'>Applied Filters</h5>
            {
              Object.keys(filters).map((key) => (
                <Badge color='primary' key={ `payment-method-filters-${key}` }>
                  <div className='d-flex justify-content-between'>
                    <span className='align-self-center'>{ key }: { filters[ key ] }</span>
                    <Button close onClick={ () => handleClickRemoveFilter(key) } />
                  </div>
                </Badge>
              ))
            }
          </div>

          <SortAndPerPageFilters
            filterValues={ filters }
            onSortFilterChange={ handleSortFilterChange }
            sortByOptions={ SORT_BY_OPTIONS }
            isLoading={ isLoading }
          />

          <Card className='py-3'>
            <VoucherRuleTable
              tableProps={{
                size       : 'sm',
                responsive : true
              }}
              voucherRules={ voucherRules }
              pagination={ pagination }
              onPageChange={ handlePageChange }
              isLoading={ isLoading }
              onTableRowClick={ handleTableRowClick }
            />
          </Card>
        </Col>
      </Row>
    </>
  )
}
VoucherListPage.propTypes    = propTypes
VoucherListPage.defaultProps = defaultProps

export default VoucherListPage
